<script setup>
import {ref, computed, onMounted, reactive} from 'vue';
import {usePortalContentAndDesignStore} from "../../../../../stores/partners/portalContentAndDesignStore";
import LoadingContent from "../components/LoadingContent.vue";
import EditableTextField from "../../../portal-editable-fields/EditableTextField.vue";
import EditableBtn from "../../../portal-editable-fields/EditableBtn.vue";
import {camelCase, scrollToTop} from "../../../../../helpers/common";
import TaxFormUpload from "./TaxFormUpload.vue";
import TaxBillingProfile from "./TaxBillingProfile.vue";
import PartnerPostbacks from "./PartnerPostbacks.vue";


const portalContentAndDesignStore = usePortalContentAndDesignStore();

const layoutStyle = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout_layout_style);
const corners = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout_layout_corners);

const isSuspended = computed(() => portalContentAndDesignStore.partner?.status === 'suspended' || false);

const partnerManager = computed(() => portalContentAndDesignStore.portalLayoutSettings.partner_manager || null);

// Tax form collection settings
const taxFormCollectionEnabled = computed(() => portalContentAndDesignStore.profilePageSettings.portal_tax_form_collection_enabled || false);
const taxForms = ref([]);
const taxFormTypes = computed(() => portalContentAndDesignStore.profilePageSettings.tax_form_types || {});

// Self-billing tax & billing profile
const selfBillingEnabled = computed(() => portalContentAndDesignStore.profilePageSettings.self_billing_enabled || false);
const taxProfile = ref(null);
const taxBillingProfileRef = ref(null);
const taxProfileVatStatuses = computed(() => portalContentAndDesignStore.profilePageSettings.tax_profile_vat_statuses || {});
const taxProfileCountries = computed(() => portalContentAndDesignStore.profilePageSettings.tax_profile_countries || []);
// Self-billing programs cannot issue a payout document without these details,
// so the copy asks for them up front; otherwise they are optional. Partners who
// cannot request payouts themselves are not told to complete them beforehand.
const payoutRequestsDisabled = computed(() => portalContentAndDesignStore.profilePageSettings.payout_requests_disabled || false);
const taxBillingDescription = computed(() => {
    const translated = portalContentAndDesignStore.translationSettings.portal_translation_profile_tax_billing_description;
    if (translated) {
        return translated;
    }

    if (!selfBillingEnabled.value) {
        return 'These details are used on your payout documents. They are optional, but adding them now keeps your payouts moving.';
    }

    return payoutRequestsDisabled.value
        ? 'These details are used to issue your payout documents (self-billing). Complete them so your payouts can be processed.'
        : 'These details are used to issue your payout documents (self-billing). Complete them before requesting a payout.';
});
const handleTaxProfileUpdated = (updated) => {
    taxProfile.value = updated;
}

// Partner-managed postbacks (S2S tracking)
const partnerPostbacksEnabled = computed(() => portalContentAndDesignStore.profilePageSettings.partner_postbacks_enabled || false);
const partnerPostbackEvents = computed(() => portalContentAndDesignStore.profilePageSettings.partner_postback_events || []);

onMounted(() => {
    portalContentAndDesignStore.initializePortalPageContent('affiliate-profile')
        .then(() => {
            form.name = portalContentAndDesignStore.authUser.name || '';
            form.surname = portalContentAndDesignStore.authUser.surname || '';
            form.email = portalContentAndDesignStore.authUser.email || '';
            form.email_notification_about_new_signups = portalContentAndDesignStore.profilePageSettings.email_notification_about_new_signups || false;
            form.email_notification_about_sales = portalContentAndDesignStore.profilePageSettings.email_notification_about_sales || false;
            form.email_notification_about_payout_requests = portalContentAndDesignStore.profilePageSettings.email_notification_about_payout_requests || false;
            form.accepts_marketing = portalContentAndDesignStore.profilePageSettings.accepts_marketing || false;
            // Load tax forms
            taxForms.value = portalContentAndDesignStore.profilePageSettings.tax_forms || [];
            // Load self-billing tax & billing profile
            taxProfile.value = portalContentAndDesignStore.profilePageSettings.tax_profile || null;
        })
})

const form = reactive({
        profile_photo: '',
        name: '',
        surname: '',
        accepts_marketing: '',
        email: '',
        password_placeholder: '',
        password: '',
        new_password: '',
        new_password_confirmation: '',
        email_notification_about_new_signups: '',
        email_notification_about_sales: '',
        email_notification_about_payout_requests: '',
})

// Tax form handlers
const handleTaxFormUploaded = (newTaxForm) => {
    // Remove any existing form of the same type
    taxForms.value = taxForms.value.filter(f => f.form_type !== newTaxForm.form_type);
    // Add the new form
    taxForms.value.unshift(newTaxForm);
}

const handleTaxFormDeleted = (taxFormId) => {
    taxForms.value = taxForms.value.filter(f => f.id !== taxFormId);
}

const formErrors = ref({});

/**
 * Get error message from form errors (handles array format from Laravel validation)
 */
const getErrorMessage = (field) => {
    const error = formErrors.value[field];
    if (Array.isArray(error)) {
        return error[0] || null;
    }
    return error || null;
};

const emailFieldVisible = ref(false);
const emailHasError = computed(() => !!formErrors.value.email);
const passwordHasErrors = computed(() =>
    !!formErrors.value.password
    || !!formErrors.value.new_password
    || !!formErrors.value.new_password_confirmation
);

function toggleEmailField() {
    emailFieldVisible.value = true;
}

const updating = ref(false);

const successUpdateMessage = ref(null);

async function updateProfile() {
    formErrors.value = {};
    successUpdateMessage.value = null;
    updating.value = true;

    try {
        // Save the tax & billing section (separate endpoint) as part of the same
        // Update action. Resolves to false when it has inline validation errors.
        const taxOk = taxBillingProfileRef.value ? await taxBillingProfileRef.value.save() : true;

        const response = await axios.post(route('partners_int.affiliate-profile.update'), form);
        const status = parseInt(response.data.status || 0);

        if (status === 0) {
            formErrors.value = response.data.errors;
        } else if (status === 1 && taxOk) {
            successUpdateMessage.value = response.data.message || 'Profile updated successfully.';
            portalContentAndDesignStore.onboardingStep = response.data.onboarding_step || 3;
            scrollToTop();
        }
        // status === 1 but tax section invalid: profile saved, tax errors shown inline.
    } catch (error) {
        if (error.response?.status === 422) {
            formErrors.value = error.response.data.errors;
        } else {
            formErrors.value = {general: 'An error occurred. Please try again.'};
            console.error(error);
        }
    } finally {
        updating.value = false;
    }
}
</script>

<template>
    <div v-if="Object.keys(portalContentAndDesignStore.profilePageSettings).length > 0">
        <!-- Suspended Notice -->
        <div
            v-if="isSuspended"
            :class="[
                layoutStyle === 'none' ? 'bg-gray-600 bg-opacity-5' : 'bg-white',
                corners,
                portalContentAndDesignStore.getPortalLayoutStyleClasses
            ]"
        >
            <div class="px-8">
                <div class="flex justify-between items-center py-14">
                  <span>
                    Your account was suspended.
                    <template v-if="portalContentAndDesignStore.profilePageSettings.suspend_reason">
                        Reason: {{ portalContentAndDesignStore.profilePageSettings.suspend_reason }}
                    </template>
                  </span>
                </div>
            </div>
        </div>

        <div v-if="successUpdateMessage"
             class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4"
             role="alert"
        >
            <span class="block sm:inline">{{ successUpdateMessage }}</span>
        </div>

        <!-- Profile Form Wrapper -->
        <div
            class="mb-10"
            :class="[
                layoutStyle !== 'none' ? 'bg-white' : '',
                corners,
                portalContentAndDesignStore.getPortalLayoutStyleClasses
            ]"
        >
            <div class="divide-y divide-gray-200" :class="layoutStyle !== 'none' ? 'px-10' : ''">
                <!-- Personal Info Section -->
                <div class="grid py-14 xl:grid-cols-3 gap-10">
                    <div class="space-y-2">
                        <editable-text-field
                            text-type="h3"
                            class="text-lg font-semibold"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_personal_info"
                        />
                    </div>

                    <div class="grid md:grid-cols-2 col-span-2 gap-8">
                        <!-- Avatar upload (not available for sub-accounts) -->
                        <template v-if="!portalContentAndDesignStore.isSubAccount">
                            <file-upload
                                :src="portalContentAndDesignStore.srcRoutes.profile_photo"
                                no-src="https://placehold.co/100x100/E5E7EB/9CA3AF?text=avatar"
                                accept="image/*"
                                :title="portalContentAndDesignStore.translationSettings.portal_translation_profile_profile_picture || 'Profile picture'"
                                name="profile_photo"
                                :upload_url="portalContentAndDesignStore.getProfileAvatarUploadRoute"
                                :delete_url="portalContentAndDesignStore.getProfileAvatarDeleteRoute"
                            />

                            <div></div>
                        </template>

                        <div class="space-y-1.5">
                            <editable-text-field
                                text-type="label"
                                for-attribute="name"
                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_first_name"
                            />
                            <text-field
                                fieldId="name"
                                :error="(formErrors.name ?? false)"
                                v-model="form.name"
                            />
                        </div>

                        <!-- Surname field (not available for sub-accounts) -->
                        <div v-if="!portalContentAndDesignStore.isSubAccount" class="space-y-1.5">
                            <editable-text-field
                                text-type="label"
                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_last_name"
                            />
                            <text-field
                                fieldId="surname"
                                :error="(formErrors.surname ?? false)"
                                v-model="form.surname"
                            />
                        </div>

                        <!-- Marketing preferences (not available for sub-accounts) -->
                        <div v-if="!portalContentAndDesignStore.isSubAccount" class="flex justify-between items-center md:col-span-2">
                            <toggle
                                name="accepts_marketing"
                                :on-class="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                                v-model="form.accepts_marketing"
                            />
                            <span class="flex flex-col flex-grow">
                                <editable-text-field
                                    text-type="span"
                                    class="text-sm font-medium"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_accept_marketing"
                                />
                            </span>
                        </div>
                    </div>
                </div>
                <!-- Your partner manager -->
                <div v-if="partnerManager" class="grid py-14 xl:grid-cols-3 gap-10">
                    <div class="space-y-2">
                        <h3 class="text-lg font-semibold">Your partner manager</h3>
                        <p class="text-sm opacity-60">Your dedicated point of contact for this program.</p>
                    </div>
                    <div class="grid col-span-2 gap-8">
                        <div class="flex items-center gap-4">
                            <div class="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-full bg-black bg-opacity-5">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="currentColor" class="opacity-70">
                                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"></path>
                                </svg>
                            </div>
                            <div class="min-w-0">
                                <div class="text-sm font-semibold">{{ partnerManager.name }}</div>
                                <a :href="`mailto:${partnerManager.email}`" class="text-sm opacity-75 hover:underline break-all">
                                    {{ partnerManager.email }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Login Details -->
                <div class="grid py-14 xl:grid-cols-3 gap-10">
                    <editable-text-field
                        text-type="h3"
                        class="text-lg font-semibold"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_login_details"
                    />

                    <div class="grid col-span-2 gap-8">
                        <div v-if="!emailHasError && !emailFieldVisible" class="flex justify-between items-end gap-4">
                            <div class="space-y-1.5 w-full">
                                <editable-text-field
                                    text-type="label"
                                    for-attribute="email"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_email_address"
                                />
                                <text-field
                                    disabled
                                    :model-value="form.email"
                                    class="w-full"
                                />
                            </div>
                            <btn custom-class="flex-shrink-0 whitespace-nowrap"
                                 type="light"
                                 @click="toggleEmailField"
                            >{{ portalContentAndDesignStore.translationSettings.portal_translation_profile_change || 'Change' }}</btn>
                        </div>

                        <div v-if="emailHasError || emailFieldVisible">
                            <div class="space-y-1.5">
                                <editable-text-field
                                    text-type="label"
                                    for-attribute="email"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_email_address"
                                />
                                <text-field
                                    type="email"
                                    field-id="email"
                                    v-model="form.email"
                                    :error="!!formErrors.email"
                                    :error-message="getErrorMessage('email')"
                                />
                            </div>
                        </div>

                        <password
                            v-if="!portalContentAndDesignStore.profilePageSettings.portal_affiliate_enable_partners_to_login_signup_via_otp"
                            v-slot="{ childScope, childMethods }"
                            :min="portalContentAndDesignStore.profilePageSettings.password_validation_rules.min"
                            :regex="portalContentAndDesignStore.profilePageSettings.password_validation_rules.regex"
                            :errors="passwordHasErrors"
                            v-cloak
                        >
                            <div v-if="!childScope.passwordFormVisible" class="flex justify-between items-end gap-4">
                                <div class="space-y-1.5 w-full">
                                    <editable-text-field
                                        text-type="label"
                                        for-attribute="password_placeholder"
                                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_password"
                                    />
                                    <text-field
                                        type="password"
                                        name="password_placeholder"
                                        field-id="password_placeholder"
                                        disabled
                                        model-value="'********'"
                                        class="w-full"
                                    />
                                </div>
                                <btn class="flex-shrink-0 whitespace-nowrap"
                                     type="light"
                                     @click="childMethods.showPasswordForm"
                                >{{ portalContentAndDesignStore.translationSettings.portal_translation_profile_change || 'Change' }}</btn>
                            </div>

                            <div v-if="childScope.passwordFormVisible">
                                <div class="mb-5">
                                    <div class="space-y-1.5">
                                        <editable-text-field
                                            text-type="label"
                                            for-attribute="password"
                                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_current_password"
                                        />
                                        <text-field
                                            type="password"
                                            name="password"
                                            field-id="password"
                                            :placeholder="portalContentAndDesignStore.translationSettings.portal_translation_profile_enter_current_password || 'Enter your current password'"
                                            v-model="form.password"
                                            :error="!!formErrors.password"
                                            :error-message="getErrorMessage('password')"
                                        />
                                    </div>
                                </div>

                                <div class="mb-5">
                                    <div class="space-y-1.5">
                                        <editable-text-field
                                            text-type="label"
                                            for-attribute="new_password"
                                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_new_password"
                                        />
                                        <text-field
                                            v-model="childScope.password"
                                            @update:model-value="form.new_password = $event"
                                            type="password"
                                            name="new_password"
                                            field-id="new_password"
                                            :placeholder="portalContentAndDesignStore.translationSettings.portal_translation_profile_new_password || 'New password'"
                                            :error="!!formErrors.new_password"
                                            :error-message="getErrorMessage('new_password')"
                                        />
                                    </div>

                                    <div class="grid grid-cols-2 gap-2 mt-5">
                                        <template v-for="(text, regex) in portalContentAndDesignStore.profilePageSettings.password_validation_rules.regex" :key="regex">
                                            <div class="flex items-center">
                                                <div
                                                    class="h-2 w-2 rounded-full mr-2"
                                                    :class="[childScope.rules[camelCase(text)] ? 'bg-black' : 'bg-gray-400']"
                                                ></div>
                                                <editable-text-field
                                                    v-if="regex === '[a-z]'"
                                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_lowercase_character"
                                                    :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                                    text-type="span"
                                                />
                                                <editable-text-field
                                                    v-else-if="regex === '[0-9]'"
                                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_one_number"
                                                    :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                                    text-type="span"
                                                />
                                                <editable-text-field
                                                    v-else-if="regex === '[A-Z]'"
                                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_one_uppercase_character"
                                                    :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                                    text-type="span"
                                                />
                                            </div>
                                        </template>

                                        <div class="flex items-center">
                                            <div
                                                class="h-2 w-2 rounded-full mr-2"
                                                :class="[childScope.rules.passwordLength ? 'bg-black' : 'bg-gray-400']"
                                            ></div>
                                            <editable-text-field
                                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_characters_minimum"
                                                :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                                text-type="span"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div class="space-y-1.5">
                                    <editable-text-field
                                        text-type="label"
                                        for-attribute="new_password_confirmation"
                                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_confirm_password"
                                    />
                                    <text-field
                                        type="password"
                                        name="new_password_confirmation"
                                        field-id="new_password_confirmation"
                                        v-model="form.new_password_confirmation"
                                        :placeholder="portalContentAndDesignStore.translationSettings.portal_translation_profile_new_password || 'New password'"
                                        :error="!!formErrors.new_password_confirmation"
                                        :error-message="getErrorMessage('new_password_confirmation')"
                                    />
                                </div>
                            </div>
                        </password>
                    </div>
                </div>
                <!-- Notifications (not available for sub-accounts) -->
                <div v-if="portalContentAndDesignStore.profilePageSettings.allow_partner_email_notifications && !portalContentAndDesignStore.isSubAccount"
                     class="grid py-14 xl:grid-cols-3 gap-10"
                >
                    <div class="space-y-2">
                        <editable-text-field
                            text-type="h3"
                            class="text-lg font-semibold"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_notifications"
                        />
                        <editable-text-field
                            class="text-sm opacity-60"
                            text-type="p"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_notification_explanation"
                        />
                    </div>
                    <div class="grid col-span-2 gap-8">
                        <div class="flex justify-between items-center md:col-span-2">
                            <toggle
                                name="email_notification_about_new_signups"
                                :on-class="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                                v-model="form.email_notification_about_new_signups"
                            />
                            <span class="flex flex-col flex-grow">
                                <editable-text-field
                                    text-type="span"
                                    class="text-sm font-medium"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_notification_signups"
                                />
                            </span>
                        </div>

                        <div class="flex justify-between items-center md:col-span-2">
                            <toggle
                                name="email_notification_about_sales"
                                :on-class="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                                v-model="form.email_notification_about_sales"
                            />
                            <span class="flex flex-col flex-grow">
                                <editable-text-field
                                    text-type="span"
                                    class="text-sm font-medium"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_notification_sales"
                                />
                            </span>
                        </div>

                        <div class="flex justify-between items-center md:col-span-2">
                            <toggle
                                name="email_notification_about_payout_requests"
                                :on-class="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                                v-model="form.email_notification_about_payout_requests"
                            />
                            <span class="flex flex-col flex-grow">
                                <editable-text-field
                                    text-type="span"
                                    class="text-sm font-medium"
                                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_profile_notification_payouts"
                                />
                            </span>
                        </div>
                    </div>
                </div>

                <div v-if="false" class="flex justify-between items-center py-14">
                    <div>
                        <h3 class="text-lg font-semibold">
                            Two-Factor Authentication
                        </h3>
                        <p class="text-sm opacity-60">
                            It is a security process where the user provides two forms of ID to log in to Partnero.
                            This
                            helps keep your account safe.
                        </p>
                    </div>
                    <div>
                        <btn type="light">Enable</btn>
                    </div>
                </div>

                <!-- Tax Forms Section (W-9/W-8BEN) -->
                <div v-if="taxFormCollectionEnabled && !portalContentAndDesignStore.isSubAccount"
                     class="grid py-14 xl:grid-cols-3 gap-10"
                >
                    <div class="space-y-2">
                        <h3 class="text-lg font-semibold">
                            {{ portalContentAndDesignStore.translationSettings.portal_translation_profile_tax_forms || 'Tax Forms' }}
                        </h3>
                        <p class="text-sm opacity-60">
                            {{ portalContentAndDesignStore.translationSettings.portal_translation_profile_tax_forms_description || 'To process payouts, we require a completed tax form. Please select the appropriate form type based on your tax residency and upload a signed copy.' }}
                        </p>
                    </div>
                    <div class="grid col-span-2 gap-8">
                        <tax-form-upload
                            :tax-forms="taxForms"
                            :tax-form-types="taxFormTypes"
                            @tax-form-uploaded="handleTaxFormUploaded"
                            @tax-form-deleted="handleTaxFormDeleted"
                        />
                    </div>
                </div>

                <!-- Tax & billing. Always collected; only mandatory when the program
                     issues self-billing documents, which need these details. -->
                <div v-if="!portalContentAndDesignStore.isSubAccount"
                     class="grid py-14 xl:grid-cols-3 gap-10"
                >
                    <div class="space-y-2">
                        <h3 class="text-lg font-semibold">
                            {{ portalContentAndDesignStore.translationSettings.portal_translation_profile_tax_billing || 'Tax &amp; billing' }}
                        </h3>
                        <p class="text-sm opacity-60">
                            {{ taxBillingDescription }}
                        </p>
                    </div>
                    <div class="grid col-span-2 gap-8">
                        <tax-billing-profile
                            ref="taxBillingProfileRef"
                            :tax-profile="taxProfile"
                            :vat-statuses="taxProfileVatStatuses"
                            :countries="taxProfileCountries"
                            :required="selfBillingEnabled"
                            @tax-profile-updated="handleTaxProfileUpdated"
                        />
                    </div>
                </div>

                <!-- Postbacks (S2S tracking) -->
                <div v-if="partnerPostbacksEnabled" class="grid py-14 xl:grid-cols-3 gap-10">
                    <div class="space-y-2">
                        <h3 class="text-lg font-semibold">Postbacks</h3>
                        <p class="text-sm opacity-60">
                            Get notified in your own tracking platform when a conversion happens. Add a postback URL
                            with placeholders and it will be called server-to-server for every selected event.
                        </p>
                    </div>
                    <div class="grid col-span-2 gap-8">
                        <partner-postbacks :events="partnerPostbackEvents" />
                    </div>
                </div>

                <div class="py-6 text-right">
                    <editable-btn
                        @click="updateProfile"
                        :type="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                        :corners="portalContentAndDesignStore.getPortalLayoutCornersClass"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_update"
                        :loading="updating"
                        :disabled="updating"
                    />
                </div>
            </div>
        </div>

        <!-- Delete Account (not available for sub-accounts) -->
        <div
            v-if="!portalContentAndDesignStore.profilePageSettings.show_onboarding && !portalContentAndDesignStore.isSubAccount"
            :class="[
                layoutStyle === 'none' ? 'bg-gray-600 bg-opacity-5' : 'bg-white',
                corners,
                portalContentAndDesignStore.getPortalLayoutStyleClasses
            ]"
        >
            <div class="px-8">
                <div class="flex justify-between items-center py-14">
                    <partner-delete
                        :delete_url="portalContentAndDesignStore.getDeleteAccountRoute"
                        :translations="{
                            deleteMyAccount: portalContentAndDesignStore.translationSettings.portal_translation_profile_change_delete_account,
                            deleteMyAccountText: portalContentAndDesignStore.translationSettings.portal_translation_profile_change_delete_account_notice,
                            areYouSure: portalContentAndDesignStore.translationSettings.portal_translation_profile_r_u_sure,
                            deleteConfirmationText: portalContentAndDesignStore.translationSettings.portal_translation_profile_delete_text,
                            typeDelete: portalContentAndDesignStore.translationSettings.portal_translation_profile_type_delete,
                            delete: portalContentAndDesignStore.translationSettings.portal_translation_general_delete,
                            cancel: portalContentAndDesignStore.translationSettings.portal_translation_general_cancel
                        }"
                    />
                </div>
            </div>
        </div>
    </div>
    <loading-content v-else />
</template>
